/**
 * Field Mapping Utility
 * Handles conversion between backend (snake_case) and frontend (camelCase) field names
 * This ensures consistent field naming across the application
 */

/**
 * Convert snake_case to camelCase
 * @param {string} str - Snake case string
 * @returns {string} Camel case string
 */
const snakeToCamel = (str) => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Convert camelCase to snake_case
 * @param {string} str - Camel case string
 * @returns {string} Snake case string
 */
const camelToSnake = (str) => {
  return str.replace(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
};

/**
 * Convert object keys from snake_case to camelCase
 * @param {Object} obj - Object with snake_case keys
 * @returns {Object} Object with camelCase keys
 */
export const mapBackendToFrontend = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(mapBackendToFrontend);
  
  const mapped = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const camelKey = snakeToCamel(key);
      mapped[camelKey] = mapBackendToFrontend(obj[key]);
    }
  }
  return mapped;
};

/**
 * Convert object keys from camelCase to snake_case
 * @param {Object} obj - Object with camelCase keys
 * @returns {Object} Object with snake_case keys
 */
export const mapFrontendToBackend = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(mapFrontendToBackend);
  
  const mapped = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const snakeKey = camelToSnake(key);
      mapped[snakeKey] = mapFrontendToBackend(obj[key]);
    }
  }
  return mapped;
};

/**
 * Standard field mappings for OAuth and broker integration
 * Maps backend field names to frontend field names
 */
export const FIELD_MAPPINGS = {
  // Token fields
  'access_token': 'accessToken',
  'refresh_token': 'refreshToken',
  'expires_at': 'expiresAt',
  'expires_in': 'expiresIn',
  'token_type': 'tokenType',
  'session_token': 'sessionToken',
  
  // User fields
  'user_id': 'userId',
  'user_name': 'userName',
  'user_email': 'userEmail',
  'user_username': 'userUsername',
  
  // Account fields
  'account_number': 'accountNumber',
  'account_type': 'accountType',
  'account_id': 'accountId',
  'account_name': 'accountName',
  'total_equity': 'totalEquity',
  'total_cash': 'totalCash',
  'buying_power': 'buyingPower',
  'option_buying_power': 'optionBuyingPower',
  'stock_buying_power': 'stockBuyingPower',
  
  // Metadata fields
  'created_at': 'createdAt',
  'updated_at': 'updatedAt',
  'last_login': 'lastLogin',
  'last_refresh': 'lastRefresh',
  'connection_date': 'connectionDate',
  'expiration_date': 'expirationDate',
  'session_expiration': 'sessionExpiration',
  
  // Broker specific
  'broker_name': 'brokerName',
  'broker_display_name': 'brokerDisplayName',
  'integration_id': 'integrationId',
  'is_sandbox': 'isSandbox',
  'is_verified': 'isVerified',
  'needs_reauth': 'needsReauth',
  
  // Status fields
  'real_time_status': 'realTimeStatus',
  'last_account_refresh': 'lastAccountRefresh',
  'selected_account': 'selectedAccount',
  'selected_account_data': 'selectedAccountData',
  
  // P&L fields
  'pnl_day': 'pnlDay',
  'pnl_total': 'pnlTotal',
  
  // Error fields
  'error_message': 'errorMessage',
  'error_code': 'errorCode',
  
  // Request/Response fields
  'redirect_uri': 'redirectUri',
  'auth_url': 'authUrl',
  'oauth_url': 'authUrl',  // Map oauth_url to authUrl for consistency
  'request_data': 'requestData',
  'response_data': 'responseData'
};

/**
 * Map specific fields using predefined mappings
 * @param {Object} obj - Object to map
 * @param {boolean} toFrontend - If true, maps to frontend (camelCase), else to backend (snake_case)
 * @returns {Object} Mapped object
 */
export const mapFields = (obj, toFrontend = true) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(item => mapFields(item, toFrontend));
  
  const mapped = {};
  const mappings = toFrontend ? FIELD_MAPPINGS : Object.fromEntries(
    Object.entries(FIELD_MAPPINGS).map(([k, v]) => [v, k])
  );
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const mappedKey = mappings[key] || key;
      mapped[mappedKey] = mapFields(obj[key], toFrontend);
    }
  }
  return mapped;
};

/**
 * Normalize token data from backend response
 * Ensures consistent field names for token data
 * @param {Object} tokenData - Raw token data from backend
 * @returns {Object} Normalized token data with consistent field names
 */
export const normalizeTokenData = (tokenData) => {
  if (!tokenData) return null;
  
  return {
    accessToken: tokenData.access_token || tokenData.accessToken,
    refreshToken: tokenData.refresh_token || tokenData.refreshToken,
    expiresAt: tokenData.expires_at || tokenData.expiresAt || tokenData.expirationDate || tokenData.sessionExpiration,
    expiresIn: tokenData.expires_in || tokenData.expiresIn,
    tokenType: tokenData.token_type || tokenData.tokenType || 'Bearer',
    scope: tokenData.scope
  };
};

/**
 * Normalize broker integration data
 * Ensures consistent field names for broker integration
 * @param {Object} integration - Raw integration data
 * @returns {Object} Normalized integration data
 */
export const normalizeBrokerIntegration = (integration) => {
  if (!integration) return null;
  
  const normalized = mapBackendToFrontend(integration);
  
  // Ensure critical fields exist with correct names
  if (normalized.credentials) {
    normalized.credentials = normalizeTokenData(normalized.credentials);
  }
  
  // Normalize user fields
  normalized.userId = normalized.userId || integration.user_id;
  normalized.userName = normalized.userName || integration.user_name;
  
  // Normalize account fields
  if (normalized.metadata?.selectedAccountData) {
    normalized.metadata.selectedAccountData = mapBackendToFrontend(
      normalized.metadata.selectedAccountData
    );
  }
  
  // Handle Tastytrade account structure
  if (normalized.accountInfo || normalized.accounts) {
    // Normalize account-number fields to accountNumber
    if (normalized.accountInfo && normalized.accountInfo['account-number']) {
      normalized.accountInfo.accountNumber = normalized.accountInfo['account-number'];
    }
    
    if (normalized.accounts && Array.isArray(normalized.accounts)) {
      normalized.accounts = normalized.accounts.map(account => {
        if (account['account-number']) {
          account.accountNumber = account['account-number'];
        }
        return account;
      });
    }
    
    // Map balances if present
    if (normalized.balances && Array.isArray(normalized.balances)) {
      normalized.balances = normalized.balances.map(balance => mapBackendToFrontend(balance));
    }
  }
  
  return normalized;
};

/**
 * Prepare data for backend API call
 * Converts frontend field names to backend format
 * @param {Object} data - Frontend data
 * @returns {Object} Backend-formatted data
 */
export const prepareForBackend = (data) => {
  return mapFrontendToBackend(data);
};

/**
 * Process backend response
 * Converts backend field names to frontend format
 * Uses FIELD_MAPPINGS for specific fields, then falls back to snake_case to camelCase conversion
 * @param {Object} response - Backend response
 * @returns {Object} Frontend-formatted response
 */
export const processBackendResponse = (response) => {
  // First apply specific field mappings
  const mappedResponse = mapFields(response, true);
  // Then apply general snake_case to camelCase conversion for any remaining fields
  return mapBackendToFrontend(mappedResponse);
};

export default {
  mapBackendToFrontend,
  mapFrontendToBackend,
  mapFields,
  normalizeTokenData,
  normalizeBrokerIntegration,
  prepareForBackend,
  processBackendResponse,
  FIELD_MAPPINGS
};